import {
  Button,
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import type { ProductVariantDTO } from "../../domain/contracts";
import { useUpdateVariant } from "../api/queries";

/** FormPrice / FormNumber ส่ง "" เมื่อช่องว่าง — ต้องแปลงก่อน z.coerce.number */
function emptyToUndefined(v: unknown) {
  if (v === "" || v === null || v === undefined) return undefined;
  return v;
}

const VariantFormSchema = z.object({
  sku: z.string().min(1, "ກະລຸນາໃສ່ SKU"),
  price: z.number().min(0, "ລາຄາຕ້ອງເປັນຕົວເລກບວກ"),
  cost: z.number().min(0, "ຕົ້ນທຶນຕ້ອງເປັນຕົວເລກບວກ"),
  compareAtPrice: z.preprocess(
    emptyToUndefined,
    z.coerce.number().min(0).optional(),
  ),
  weightGrams: z.preprocess(
    emptyToUndefined,
    z.coerce.number().min(0).optional(),
  ),
});

type VariantFormValues = z.infer<typeof VariantFormSchema>;

interface VariantFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  variant: ProductVariantDTO;
}

export function VariantFormDialog({
  open,
  onOpenChange,
  productId,
  variant,
}: VariantFormDialogProps) {
  const updateVariant = useUpdateVariant(productId, variant.id);

  const methods = RHF.useForm<VariantFormValues>({
    resolver: zodResolver(VariantFormSchema),
    defaultValues: {
      sku: variant.sku,
      price: variant.price,
      cost: variant.cost ?? 0,
      compareAtPrice: variant.compareAtPrice,
      weightGrams: variant.weightGrams ?? undefined,
    },
  });

  useEffect(() => {
    methods.reset({
      sku: variant.sku,
      price: variant.price,
      cost: variant.cost ?? 0,
      compareAtPrice: variant.compareAtPrice,
      weightGrams: variant.weightGrams ?? undefined,
    });
  }, [variant, methods]);

  const onSubmit = (values: VariantFormValues) => {
    updateVariant.mutate(values, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <FormRoot methods={methods} onSubmit={onSubmit}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ແກ້ໄຂ Variant</h3>

          {/* Option Values Display */}
          {variant.optionValuesDetails &&
            variant.optionValuesDetails.length > 0 && (
              <div>
                <label
                  htmlFor="options"
                  className="mb-2 block font-medium text-sm"
                >
                  Options
                </label>
                <div className="flex flex-wrap gap-2">
                  {variant.optionValuesDetails.map((opt, idx) => (
                    <span
                      key={idx}
                      className="rounded-lg border bg-muted px-3 py-1.5 text-sm"
                    >
                      <span className="font-medium text-muted-foreground">
                        {opt.optionName?.lo || opt.optionName?.en}:
                      </span>{" "}
                      {opt.valueName?.lo || opt.valueName?.en}
                    </span>
                  ))}
                </div>
              </div>
            )}

          <FormInput
            name="sku"
            label="SKU"
            placeholder="PRODUCT-SLUG-RED-S"
            required
          />

          <FormPrice
            name="price"
            label="ລາຄາ (LAK)"
            currencySymbol="₭"
            placeholder="100000"
            required
          />

          <FormPrice
            name="cost"
            label="ຕົ້ນທຶນ (LAK)"
            currencySymbol="₭"
            placeholder="0"
            hint="ລາຄາຕົ້ນທຶນຕໍ່ຫນ່ວຍ"
          />

          <FormPrice
            name="compareAtPrice"
            label="ລາຄາເກົ່າ (LAK)"
            currencySymbol="₭"
            placeholder="150000"
            hint="ລາຄາກ່ອນຫຼຸດ (optional)"
          />

          <FormNumber
            name="weightGrams"
            label="ນໍ້າໜັກ (ກຣາມ)"
            placeholder="250"
            hint="Default: 250g"
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateVariant.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={updateVariant.isPending}>
              {updateVariant.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </div>
      </FormRoot>
    </Modal>
  );
}
